import { BrowserMultiFormatReader } from '@zxing/browser'
import { NotFoundException } from '@zxing/library'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { captureError, trackEvent } from '@/lib/monitoring'
import { Button } from './button'

interface BarcodeScannerProps {
  onResult: (code: string) => void
  onError?: (error: string) => void
  onClose: () => void
  isOpen: boolean
}

export function BarcodeScanner({ onResult, onError, onClose, isOpen }: BarcodeScannerProps) {
  const { t } = useTranslation()
  const videoRef = useRef<HTMLVideoElement>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const codeReaderRef = useRef<BrowserMultiFormatReader | null>(null)
  const scannerControlsRef = useRef<{ stop: () => void } | null>(null)
  const hasFoundCodeRef = useRef<boolean>(false)
  const isScanningRef = useRef<boolean>(false)

  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isSuccess, setIsSuccess] = useState(false)

  const stopScanning = useCallback(() => {
    // Mark as not scanning to prevent further processing
    isScanningRef.current = false

    // Stop the scanner controls first
    if (scannerControlsRef.current) {
      try {
        scannerControlsRef.current.stop()
      } catch (_error) {
        // Ignore errors during stop
      }
      scannerControlsRef.current = null
    }

    // Stop code reader
    if (codeReaderRef.current) {
      codeReaderRef.current = null
    }

    // Stop video stream
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => {
        track.stop()
      })
      streamRef.current = null
    }

    // Clear video element
    if (videoRef.current) {
      videoRef.current.srcObject = null
    }

    // Reset the flags
    hasFoundCodeRef.current = false

    setIsLoading(false)
    setError(null)
    setIsSuccess(false)
  }, [])

  const startScanning = useCallback(async () => {
    // Prevent multiple simultaneous scans
    if (isScanningRef.current) {
      return
    }

    const startTime = performance.now()
    try {
      setError(null)
      setIsLoading(true)
      isScanningRef.current = true
      hasFoundCodeRef.current = false

      // Check if camera is available
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error(t('books.scanner.noCameraSupport'))
      }

      // Stop any existing reader first
      if (codeReaderRef.current) {
        codeReaderRef.current = null
      }

      // Initialize code reader
      const codeReader = new BrowserMultiFormatReader()
      codeReaderRef.current = codeReader

      // Use environment facing camera if available (back camera on mobile)
      let deviceId: string | undefined

      try {
        const devices = await navigator.mediaDevices.enumerateDevices()
        const videoDevices = devices.filter((device) => device.kind === 'videoinput')

        // Prefer back camera on mobile (usually better for scanning)
        const backCamera = videoDevices.find(
          (device) =>
            device.label.toLowerCase().includes('back') ||
            device.label.toLowerCase().includes('rear') ||
            device.label.toLowerCase().includes('environment')
        )

        if (backCamera) {
          deviceId = backCamera.deviceId
        }
      } catch (_error) {
        // If device enumeration fails, use default camera
        deviceId = undefined
      }

      // Start scanning with preferred camera
      const controls = await codeReader.decodeFromVideoDevice(
        deviceId,
        videoRef.current!,
        (result, error) => {
          // CRITICAL: Check if we should still be scanning
          if (!isScanningRef.current || hasFoundCodeRef.current) {
            return
          }

          if (result) {
            const code = result.getText()
            // Validate that it looks like an ISBN (10 or 13 digits)
            if (/^\d{10}(\d{3})?$/.test(code.replace(/[-\s]/g, ''))) {
              // Mark as found to prevent further processing
              hasFoundCodeRef.current = true

              // Show success state briefly before closing
              setIsSuccess(true)

              // Use setTimeout to ensure the callback doesn't get called again immediately
              setTimeout(() => {
                if (hasFoundCodeRef.current) {
                  const scanTime = performance.now() - startTime
                  trackEvent('barcode_scan_success', {
                    scan_time_ms: Math.round(scanTime),
                  })
                  const cleanCode = code.replace(/[-\s]/g, '')
                  stopScanning()
                  onResult(cleanCode)
                  // Close after showing success
                  setTimeout(() => {
                    onClose()
                  }, 500)
                }
              }, 0)
              return
            }
          }

          // Ignore NotFoundException - it's expected when no barcode is found
          if (error && !(error instanceof NotFoundException)) {
            // Silent fail - expected behavior when scanning
          }
        }
      )

      // Store the scanner controls
      scannerControlsRef.current = controls

      // Capture the stream reference after it's set
      if (videoRef.current?.srcObject) {
        streamRef.current = videoRef.current.srcObject as MediaStream
      }

      setIsLoading(false)
    } catch (err) {
      if (err instanceof Error) {
        let errorType = 'unknown'

        // Don't treat AbortError as a real error - it's expected when video is interrupted
        if (err.name === 'AbortError') {
          // Just reset state without showing error
          setIsLoading(false)
          isScanningRef.current = false
          return
        }

        if (err.name === 'NotAllowedError') {
          errorType = 'permission_denied'
          setError(t('books.scanner.permissionDenied'))
        } else if (err.name === 'NotFoundError') {
          errorType = 'no_camera'
          setError(t('books.scanner.noCameraFound'))
        } else if (err.name === 'NotSupportedError') {
          errorType = 'not_supported'
          setError(t('books.scanner.noCameraSupport'))
        } else {
          errorType = 'unknown'
          setError(err.message || t('books.scanner.unknownError'))
        }

        trackEvent('barcode_scan_failed', { error_type: errorType })
        captureError(err, { context: 'barcode_scanner' })
      } else {
        setError(t('books.scanner.unknownError'))
      }

      if (onError) {
        onError(err instanceof Error ? err.message : t('books.scanner.unknownError'))
      }

      setIsLoading(false)
      isScanningRef.current = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onClose, onError, onResult, stopScanning, t])

  // biome-ignore lint/correctness/useExhaustiveDependencies: startScanning would cause infinite loop
  useEffect(() => {
    if (!isOpen) {
      stopScanning()
      return
    }

    // Prevent multiple simultaneous initialization attempts
    const timeoutId = setTimeout(() => {
      if (isOpen && !isScanningRef.current) {
        startScanning()
      }
    }, 100)

    return () => {
      clearTimeout(timeoutId)
      stopScanning()
    }
  }, [isOpen, stopScanning])

  // Cleanup effect to ensure camera is stopped when component unmounts
  useEffect(() => {
    return () => {
      stopScanning()
    }
  }, [stopScanning])

  if (!isOpen) {
    return null
  }

  return (
    <div className="fixed inset-0 z-50 bg-black bg-opacity-90 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg p-4 w-full max-w-lg mx-auto">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium text-gray-900">{t('books.scanner.title')}</h3>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            ✕
          </Button>
        </div>

        {error ? (
          <div className="text-center py-8">
            <div className="text-red-600 mb-4">
              <svg
                className="mx-auto h-12 w-12"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.982 16.5c-.77.833.192 2.5 1.732 2.5z"
                />
              </svg>
            </div>
            <p className="text-red-600 mb-4">{error}</p>
            <div className="space-y-2">
              <Button type="button" onClick={startScanning} variant="outline" className="w-full">
                {t('books.scanner.tryAgain')}
              </Button>
              <Button type="button" onClick={onClose} variant="outline" className="w-full">
                {t('common.cancel')}
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="relative">
              <video
                ref={videoRef}
                className="w-full h-80 bg-gray-900 rounded-lg object-cover"
                style={{ transform: 'scaleX(1)' }}
                playsInline
                muted
                autoPlay
                controls={false}
                webkit-playsinline="true"
              />

              {isLoading && (
                <div className="absolute inset-0 flex items-center justify-center bg-gray-900 bg-opacity-75 rounded-lg">
                  <div className="text-white text-center">
                    <div className="animate-spin h-8 w-8 border-2 border-white border-t-transparent rounded-full mx-auto mb-2" />
                    <div className="text-sm">{t('books.scanner.startingCamera')}</div>
                  </div>
                </div>
              )}

              {!isLoading && !isSuccess && (
                <>
                  {/* Scanning overlay with guide box */}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="relative">
                      {/* Corner guides */}
                      <div className="w-48 h-24 border-2 border-white border-dashed rounded-lg relative">
                        {/* Top left corner */}
                        <div className="absolute -top-1 -left-1 w-6 h-6 border-l-4 border-t-4 border-green-400 rounded-tl-lg"></div>
                        {/* Top right corner */}
                        <div className="absolute -top-1 -right-1 w-6 h-6 border-r-4 border-t-4 border-green-400 rounded-tr-lg"></div>
                        {/* Bottom left corner */}
                        <div className="absolute -bottom-1 -left-1 w-6 h-6 border-l-4 border-b-4 border-green-400 rounded-bl-lg"></div>
                        {/* Bottom right corner */}
                        <div className="absolute -bottom-1 -right-1 w-6 h-6 border-r-4 border-b-4 border-green-400 rounded-br-lg"></div>
                      </div>
                    </div>
                  </div>

                  <div className="absolute bottom-4 left-4 right-4">
                    <div className="bg-black bg-opacity-50 text-white text-center py-2 px-4 rounded-lg">
                      <div className="text-sm font-medium">{t('books.scanner.scanning')}</div>
                      <div className="text-xs mt-1 opacity-80">
                        {t('books.scanner.positionBarcode')}
                      </div>
                    </div>
                  </div>
                </>
              )}

              {isSuccess && (
                <div className="absolute inset-0 flex items-center justify-center bg-green-500 bg-opacity-75 rounded-lg">
                  <div className="text-white text-center">
                    <div className="w-16 h-16 mx-auto mb-4 bg-green-600 rounded-full flex items-center justify-center">
                      <svg
                        className="w-8 h-8"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                    </div>
                    <div className="text-lg font-medium">{t('books.scanner.codeFound')}</div>
                    <div className="text-sm opacity-90">{t('books.scanner.processing')}</div>
                  </div>
                </div>
              )}
            </div>

            <div className="text-center">
              <p className="text-sm text-gray-600 mb-4">{t('books.scanner.instructions')}</p>
              <Button type="button" onClick={onClose} variant="outline" className="w-full">
                {t('common.cancel')}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
